import { FormatedError, Formatter, ErrorActions } from '../types/errors';

type ErrorResponseData = {
  message?: string;
  error?: string;
  statusCode?: number;
};

type ErrorWithResponse = {
  response?: {
    data?: ErrorResponseData;
  };
  cause?: unknown | (() => unknown);
  message?: string;
};

const resolveCause = (value: ErrorWithResponse): unknown => {
  if (typeof value.cause === 'function') {
    return value.cause();
  }
  return value.cause;
};

const getResponseData = (value: unknown): ErrorResponseData | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const errorValue = value as ErrorWithResponse;
  const responseData = errorValue.response?.data;
  if (responseData && typeof responseData === 'object') {
    return responseData;
  }

  const cause = resolveCause(errorValue);
  if (cause && cause !== value) {
    return getResponseData(cause);
  }

  // Plain API payload (not an Error instance — Errors always have .message)
  if (!(value instanceof Error) && ('message' in value || 'error' in value || 'statusCode' in value)) {
    return value as ErrorResponseData;
  }

  return undefined;
};

const pickResponseMessage = (data?: ErrorResponseData): string => {
  if (!data) {
    return '';
  }
  if (typeof data.message === 'string' && data.message) {
    return data.message;
  }
  if (typeof data.error === 'string' && data.error) {
    return data.error;
  }
  return '';
};

/** Resolve API/SDK error text from response payloads nested under `cause` when needed. */
export const getApiErrorMessage = (value: unknown): string => {
  const responseMessage = pickResponseMessage(getResponseData(value));
  if (responseMessage) {
    return responseMessage;
  }
  if (value instanceof Error) {
    return value.message || '';
  }
  return typeof value === 'string' ? value : '';
};

export const getFormatedError = (value: unknown, translate: Formatter): FormatedError => {
  const message = getApiErrorMessage(value);

  const result: FormatedError = {
    message: 'Error. Please try again.',
  };
  if (value === 'Unauthorized') {
    result.message = '';
    result.action = ErrorActions.Expired;
  } else if (value === 'Invalid verification code') {
    result.message = 'Invalid verification code';
  } else if (!navigator.onLine) {
    result.message = 'Network Error. Please check your connection and try again.';
  } else if (['Invalid username and/or password', 'Wrong credentials'].some(error => message.includes(error))) {
    result.message = translate('login.login.The email and password combination is not valid');
  } else if (message === 'Access suspended') {
    result.message = 'Account access is suspended. Please contact OneReach.ai support.';
  } else if (message === 'Two Factor token invalid') {
    result.message = 'Verification code is invalid or expired. Please try again.';
  } else if (message.includes('account is missing or disabled') || message.includes('Account is disabled')) {
    result.message = translate('multi_user.list_users.accountDisabled');
  } else if (message.includes('Incorrect Captcha: low score')) {
    result.message = '';
    result.action = ErrorActions.RenderCaptcha;
  } else if (message.includes('Incorrect Captcha')) {
    result.message = 'Error verifying reCAPTCHA. Please try again.';
  } else if (message.includes('You are allowed')) {
    result.message = message;
  } else if (message === 'You have exceeded the request limit') {
    result.message = translate('multi_user.list_users.Too many requests');
  } else if (message === 'Passkey not found' || value === 'Passkey not found') {
    result.message = translate('login.login.Passkey not found');
  }

  return result;
};
